import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isStandalone, isIos } from "@/lib/webPush";
import { hasInstallPrompt, onInstallPromptChange, promptInstall } from "@/lib/pwaInstall";
import { whenNoDialog } from "@/lib/modalGate";
import { APP_STORE_URL, AppleIcon } from "@/components/AppStoreBadges";
import { Illustration } from "@/components/Illustration";

const DISMISSED_KEY = "edufocal-pwa-install-dismissed";
const REMIND_AT_KEY = "edufocal-pwa-install-remind-at";
const REMIND_DAYS = 7;

// Invites logged-in browser users to get EduFocal on their device.
// iOS is sent to the native app on the App Store; Android/Chrome gets the PWA install prompt
// until the Play Store build ships.
export function PwaInstallModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [canPrompt, setCanPrompt] = useState(hasInstallPrompt());
  const [installing, setInstalling] = useState(false);

  useEffect(() => onInstallPromptChange(() => setCanPrompt(hasInstallPrompt())), []);

  useEffect(() => {
    if (!user || isStandalone()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;
    const remindAt = Number(localStorage.getItem(REMIND_AT_KEY) || 0);
    if (remindAt && Date.now() < remindAt) return;

    // Wait for the login transition, then wait for any other popup (grade check, announcement) to close.
    let cancel: (() => void) | null = null;
    const timer = setTimeout(() => {
      // Only show when we can actually help: a real install prompt, or iOS
      // where the native app is the answer. Other browsers get nothing.
      if (hasInstallPrompt() || isIos()) cancel = whenNoDialog(() => setOpen(true), { everyMs: 5000, maxWaitMs: 180000 });
    }, 8000);
    return () => { clearTimeout(timer); cancel?.(); };
  }, [user?.id]);

  const dismissForever = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setOpen(false);
  };

  const remindLater = () => {
    localStorage.setItem(REMIND_AT_KEY, String(Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000));
    setOpen(false);
  };

  const install = async () => {
    setInstalling(true);
    try {
      const accepted = await promptInstall();
      if (accepted) {
        localStorage.setItem(DISMISSED_KEY, "1");
        setOpen(false);
      }
    } finally {
      setInstalling(false);
    }
  };

  if (!open) return null;

  const ios = isIos();

  return (
    <Dialog open onOpenChange={(v) => { if (!v) remindLater(); }}>
      <DialogContent className="max-w-sm w-full p-0 overflow-hidden gap-0 flex flex-col">
        <div className="bg-primary/5 border-b px-6 pt-6 pb-5 text-center">
          <Illustration name="owl-phone" size={120} className="mx-auto" />
          <h2 className="mt-3 text-xl font-bold leading-tight">{ios ? "EduFocal Academy is on the App Store" : "Get the EduFocal app"}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {ios
              ? "Quizzes, lessons, duels and your streak, built for iPhone and iPad."
              : "Add EduFocal Academy to your device and you're one tap from practice."}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {ios ? (
            <p className="text-sm text-muted-foreground">
              Tap the button below to open the App Store, install it, then sign in with the details you use here.
            </p>
          ) : canPrompt ? (
            <p className="text-sm text-muted-foreground">
              Tap the button below and EduFocal gets its own icon on your home screen.
            </p>
          ) : (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Here's how to add it:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Tap the <span className="font-semibold text-foreground">Share</span> button in Safari</li>
                <li>Scroll down and tap <span className="font-semibold text-foreground">Add to Home Screen</span></li>
                <li>Tap <span className="font-semibold text-foreground">Add</span>. That's it!</li>
              </ol>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 space-y-2">
          {ios && (
            <Button asChild className="w-full rounded-lg" data-testid="button-app-store">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={remindLater}>
                <AppleIcon className="w-4 h-4 mr-2" />
                Download on the App Store
              </a>
            </Button>
          )}
          {!ios && canPrompt && (
            <Button
              onClick={install}
              disabled={installing}
              className="w-full rounded-lg"
              data-testid="button-pwa-install"
            >
              {installing ? "Opening..." : "Install app"}
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={remindLater}
              className="flex-1"
              data-testid="button-pwa-remind-later"
            >
              Remind me later
            </Button>
            <Button
              variant="ghost"
              onClick={dismissForever}
              className="flex-1"
              data-testid="button-pwa-dismiss"
            >
              No thanks
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
